import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { preloadCreateSpace } from '@confluence/create-space-preload/entry-points/preloadCreateSpace.ts';
import { fg } from '@confluence/feature-gating';
import { preloadPlatformNavigationQuery } from '@confluence/side-navigation/entry-points/preloadPlatformNavigationQuery.ts';
import { preloadInvitedAppShortcutsRecommendationsQuery } from '@confluence/side-navigation/entry-points/preloadInvitedAppShortcutsRecommendationsQuery.ts';
import { preloadSpacesList } from '@confluence/space-utils/entry-points/preloadSpacesList.ts';

import { preloadForYou } from './HomeViews/ForYou/preloadForYou.tsx';
import { preloadRecent } from './HomeViews/Recent/preloadRecent.tsx';
import { preloadStarred } from './HomeViews/Starred/preloadStarred.tsx';
import { preloadDrafts } from './HomeViews/Drafts/preloadDrafts.ts';
import { View } from './homeRouteParams.ts';

type MatchParams = Record<string, string>;

type PreloadHomeOptions = {
	cloudId: string | null;
	isLicensed: boolean;
	userId: string | null;
	matchParams?: MatchParams;
	isWhiteboardViewedEnabled?: boolean;
	isDatabaseEnabled?: boolean;
	isSlidesEnabled?: boolean;
};

const getSelectedView = (matchParams?: MatchParams) => {
	const view = matchParams?.view as View;
	return Object.values(View).includes(view) ? view : View.OVERVIEW;
};

const preloadSelectedView = ({
	cloudId,
	isLicensed,
	userId,
	matchParams,
	isWhiteboardViewedEnabled = false,
	isDatabaseEnabled = false,
	isSlidesEnabled = false,
}: PreloadHomeOptions) => {
	if (!cloudId) {
		return Promise.resolve({});
	}
	const isLoggedIn = Boolean(userId);
	const selectedView = getSelectedView(matchParams);
	switch (selectedView) {
		case View.RECENT:
			return preloadRecent({
				cloudId,
				isLoggedIn,
				isWhiteboardViewedEnabled,
				isDatabaseEnabled,
			});
		case View.STARRED:
			return preloadStarred({ isLoggedIn });
		case View.DRAFTS:
			return preloadDrafts({ isLoggedIn });
		case View.OVERVIEW:
		default:
			return preloadForYou({
				userId,
				cloudId,
				isLicensed,
				isWhiteboardViewedEnabled,
				isDatabaseEnabled,
				isSlidesEnabled,
			});
	}
};

export const preloadHome = (options: PreloadHomeOptions): Promise<void | any[] | Error[]> => {
	return waitForAllReturnWhenError([
		createDataFetcherForSuspense('side-navigation', () => {
			const sideNavTasks = [
				preloadSpacesList({
					//preloads left nav
					isLicensed: options.isLicensed,
				}),
			];
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			if (fg('navx-global-side-nav-confluence')) {
				sideNavTasks.push(preloadPlatformNavigationQuery());
				// No-ops unless both cloudId and userId are set.
				sideNavTasks.push(preloadInvitedAppShortcutsRecommendationsQuery());
			}
			return sideNavTasks;
		}),
		preloadCreateSpace({ isLicensed: options.isLicensed }),
		preloadSelectedView(options),
	]);
};
